import { log } from '@/services/Log'

type variantOptionsValue = {
  option_id: number
  id: number
}

type OptionsMap = { [key: number]: number }
type HasOptionsValues = {
  sku: string,
  option_values: Array<variantOptionsValue>
}

const getVariantOptionsMap = (variant: HasOptionsValues): OptionsMap => (
  variant.option_values.reduce((acc, optionValue) => ({
    ...acc,
    [optionValue.option_id]: optionValue.id,
  }), {})
)

type SkusOptionValuesMap = { [key: string]: OptionsMap }
const getProductVariantOptionsMap = (variants: HasOptionsValues[]): SkusOptionValuesMap => (
  variants.reduce((acc, variant) => ({
    ...acc,
    [variant.sku]: getVariantOptionsMap(variant),
  }), {})
)

const scoreMatchBetweenOptionsSets = (target: OptionsMap) => (option: OptionsMap) => (
  Object.entries(target).reduce((score, [optionId, optionValue]) => (
    score + (
      option[optionId] === optionValue ? 1 : 0
    )
  ), 0)
)

const getSkusLinks = (
  optionsValueIds: { [otionId: number]: number[] },
  skusOptionsMap: SkusOptionValuesMap,
  currentSku: string,
) => (
  Object.entries(optionsValueIds)
    .reduce((options, [optionId, optionValuesIds]) => ({
      ...options,
      [optionId]: optionValuesIds.reduce((optionValues, optionSearchingId) => {
        const idealSetOfOptionValues = {
          ...skusOptionsMap[currentSku],
          [optionId]: optionSearchingId,
        }
        const skuHasTargetOptionValue = ([, skuMap]: [string, OptionsMap]) => (
          skuMap[optionId] === optionSearchingId
        )
        const matchScoreTableForSkusAndOptions = (
          Object.entries(skusOptionsMap)
            .filter(skuHasTargetOptionValue)
            .map(([sku, candidateOptions]): [string, number] => (
              [sku, scoreMatchBetweenOptionsSets(idealSetOfOptionValues)(candidateOptions)]
            ))
        )
        const hasAtLeastOneMatchedOption = ([, score]: [unknown, number]) => score > 0
        const highestScoredSkuForTargetOptionValue = (
          matchScoreTableForSkusAndOptions
            .filter(hasAtLeastOneMatchedOption)
            .sort(([, scoreA], [, scoreB]) => (
              scoreB - scoreA
            ))?.[0]?.[0] || null
        )
        if (!highestScoredSkuForTargetOptionValue) {
          return optionValues
        }
        return {
          ...optionValues,
          [optionSearchingId]: highestScoredSkuForTargetOptionValue,
        }
      }, {}),
    }), {})
)

type MinimalOptionsProps = {
  id: number,
  option_values: { id: number }[]
}

type optionsIdToValuesIds = {
  [optionId: number]: number[]
}
const getInitialOptions = (): optionsIdToValuesIds => ({})
const optionsToOptionsMap = (optionsInput: MinimalOptionsProps[]) => (
  optionsInput.reduce((acc, option) => ({
    ...acc,
    [option.id]: option.option_values.map((ov) => ov.id),
  }), getInitialOptions())
)

type skuTarget = string
export type ProductOptionsToSkuMap = {
  [optionId: number]: {
    [optionValueId: number]: skuTarget
  }
}
export const productToOptionMapFetcher = ({
  product,
  targetSku,
}: {
  product: {
    variants: HasOptionsValues[]
    options: MinimalOptionsProps[]
  },
  targetSku: string
}): ProductOptionsToSkuMap => {
  log({
    product,
    targetSku,
  })
  const variantsSkuToOptions = getProductVariantOptionsMap(product.variants)
  const options = optionsToOptionsMap(product.options)
  return getSkusLinks(options, variantsSkuToOptions, targetSku)
}

export default productToOptionMapFetcher
